import { useState, useEffect, useContext, useRef } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import useLocalStorageState from 'use-local-storage-state';
import { setDelogueApiToken } from 'lib/api/utils';
import { type AuthContext, AuthTokenContext } from 'features/auth/hooks/authContext';
import {
  SelectedOrgContext,
  SELECTED_ORG_AUTH0_ID_KEY,
  ACTIVE_ORG_UNSUPPORTED_SENTINEL,
  readActiveOrgCookie,
  writeActiveOrgCookie,
  clearActiveOrgCookie,
} from 'features/auth/hooks/orgContext';
import { useLoginUsers } from 'features/auth/hooks/federatedAuthHooks';
import { takeImpersonationToken } from 'features/auth/utils/legacyOrgHint';

// DL-64690: read at module scope, before React mounts and before the Auth0 SDK rewrites the URL,
// so the fragment is consumed exactly once per document load.
const impersonationToken = takeImpersonationToken();

const AuthTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [authContext, setAuthContext] = useState<AuthContext>({
    isLoading: true,
    isAuthenticated: false,
    token: null,
  });

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      setDelogueApiToken(null);
      setAuthContext({ isLoading, isAuthenticated, token: null });
      return;
    }
    const fetchToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setDelogueApiToken(accessToken);
        setAuthContext({ isLoading, isAuthenticated, token: accessToken });
      } catch {
        setDelogueApiToken(null);
        setAuthContext({ isLoading: false, isAuthenticated: false, token: null });
      }
    };
    fetchToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return <AuthTokenContext.Provider value={authContext}>{children}</AuthTokenContext.Provider>;
};

// Consecutive org-token failures tolerated before the org is rejected for the session.
const MAX_ORG_TOKEN_FAILURES = 3;

// Decode a claim from a JWT without verifying the signature (client-side only).
const getJwtClaim = (token: string, claim: string): string | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));
    const value: unknown = payload?.[claim];
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
};

const getJwtOrgId = (token: string): string | null => getJwtClaim(token, 'org_id');

// localStorage key remembering WHICH Auth0 subject (user) the persisted query cache belongs to.
// An identity change is a session event exactly like an org change (jam 34c2d9c7 retriggered on
// test-o, DL-61837): legacy logout + fresh legacy login as a DIFFERENT user in the SAME org never
// changes selectedOrgAuth0Id, so the org-transition teardown below doesn't fire — and the
// IndexedDB-persisted query cache keeps serving the PREVIOUS user's data under the same keys for
// up to 24h with zero refetches. Persisted (not in-memory) because the swap typically happens
// across page loads, which is exactly when the persisted cache is the thing that goes stale.
const LAST_AUTH_SUBJECT_KEY = 'delogue-last-auth-subject';

// OrgProvider runs inside AuthTokenProvider so it can read AuthTokenContext.
const OrgProvider = ({ children }: { children: React.ReactNode }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAuthenticated, isLoading, token: initialToken } = useContext(AuthTokenContext);

  const [selectedOrgAuth0Id, setSelectedOrgAuth0Id, { removeItem }] = useLocalStorageState<
    string | null
  >(SELECTED_ORG_AUTH0_ID_KEY, { defaultValue: null });
  const [orgToken, setOrgToken] = useState<string | null>(null);

  // Orgs that have failed to resolve to a Delogue user this session — either the accessible-orgs
  // validation below rejected them, or the user backed out of one via clearSelectedOrg. Without
  // this, auto-detect re-selects the token's org_id immediately after validation clears it (when
  // the backend's org list carries no matching auth0OrgId), ping-ponging between the two effects —
  // gated queries fire in the windows where orgToken is set and fail against the backend (DL-61312).
  //
  // Tracked in BOTH a ref and state: `removeItem()` (from useLocalStorageState) triggers a
  // synchronous useSyncExternalStore update, which can commit a re-render of this component
  // BEFORE a same-tick setRejectedOrgIds() call has been applied. The auto-detect effect below
  // must read the ref (mutated synchronously, immediately visible, no batching/ordering race) to
  // decide whether to reselect; the state copy exists only so context consumers (e.g.
  // SwitchUserFederated) re-render when the rejected set changes.
  const rejectedOrgIdsRef = useRef<Set<string>>(new Set());
  const [rejectedOrgIds, setRejectedOrgIds] = useState<Set<string>>(new Set());

  // Logout-in-flight flag (Slack report, DL-61837): useLogout clears the selected org while its
  // /betalogin/logout round trip is still running, and "authenticated + no org" otherwise renders
  // the account selector — flashing it mid-logout. Never reset: the flow always ends in a
  // top-level navigation (Auth0 /v2/logout or the SDK fallback), which tears this tree down.
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const beginLogout = () => setIsLoggingOut(true);
  const rejectOrgId = (auth0OrgId: string) => {
    if (rejectedOrgIdsRef.current.has(auth0OrgId)) return;
    rejectedOrgIdsRef.current = new Set(rejectedOrgIdsRef.current).add(auth0OrgId);
    setRejectedOrgIds(rejectedOrgIdsRef.current);
  };

  // Consecutive getAccessTokenSilently failures per org. The catch below used to only
  // removeItem(): the auto-detect effect then re-selected the same org from the token's
  // org_id claim and retried instantly — an invisible loop hammering Auth0's /authorize
  // several times per second when the org-scoped token can never be issued (third-party
  // cookies blocked, consent_required, Auth0 outage — DL-61312). A couple of retries
  // absorb transient failures; after that the org is rejected so the loader surfaces a
  // dead end instead of spinning.
  const orgTokenFailuresRef = useRef<Map<string, number>>(new Map());

  // Always-current selection, so the async token fetch below can detect that the selection
  // moved on while it was in flight and discard its (now wrong-org) result. Without this, the
  // stale resolution lands under the NEW org: its token is handed to the query layer, the
  // old org's /api/users/me response is cached under the new org's query key, and — because
  // the query cache is PERSISTED (IndexedDB, 24h staleTime/maxAge) — the wrong-org data then
  // survives every subsequent page load with no refetch. This is exactly how a legacy-side
  // Switch-Company (R6 cookie adoption at load time) rendered the previous org's user
  // indefinitely (DL-61837 QA regression, hg's 8-step repro).
  const selectedOrgRef = useRef<string | null>(selectedOrgAuth0Id);
  selectedOrgRef.current = selectedOrgAuth0Id;

  // An org transition — D2's own selectOrg, the R6 cookie adoption below, a membership
  // rejection, or the unsupported-context sign-out — ends the previous org's session
  // (decided on DL-61837: a switch is a session event, not a data refresh). Tear the old
  // session down: drop its tokens and clear ALL cached queries, in-memory and persisted.
  // Org-less query keys (styles-data, feature-flags, logged-in-user, …) would otherwise keep
  // serving the previous org's data for up to 24h out of the IndexedDB persister.
  const queryClient = useQueryClient();
  const prevOrgRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevOrgRef.current;
    prevOrgRef.current = selectedOrgAuth0Id;
    if (prev && selectedOrgAuth0Id !== prev) {
      setOrgToken(null);
      setDelogueApiToken(null);
      queryClient.clear();
    }
  }, [selectedOrgAuth0Id, queryClient]);

  // Identity transition teardown (jam 34c2d9c7 retriggered, DL-61837): a DIFFERENT user on the
  // same browser — e.g. legacy logout + fresh legacy login as user B in the SAME org — leaves
  // selectedOrgAuth0Id untouched, so the org teardown above never fires, and the persisted query
  // cache keeps rendering user A's data while every token already belongs to user B. Compare the
  // bare token's `sub` with the persisted last-known subject and clear everything on mismatch.
  // Runs before org-scoped queries can fire: they gate on orgToken, which is only fetched after
  // isAuthenticated/initialToken settle — the same render this effect observes.
  useEffect(() => {
    if (!isAuthenticated || !initialToken) return;
    const sub = getJwtClaim(initialToken, 'sub');
    if (!sub) return;
    const prevSub = localStorage.getItem(LAST_AUTH_SUBJECT_KEY);
    if (prevSub && prevSub !== sub) {
      setOrgToken(null);
      setDelogueApiToken(null);
      queryClient.clear();
    }
    if (prevSub !== sub) localStorage.setItem(LAST_AUTH_SUBJECT_KEY, sub);
  }, [isAuthenticated, initialToken, queryClient]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      setOrgToken(null);
      return;
    }
    if (!selectedOrgAuth0Id) {
      setOrgToken(null);
      // Auto-detect org from the initial token — handles the >20-org Auth0 redirect-back case.
      // Skipped while the shared cookie holds the unsupported-context sentinel: the sentinel
      // sign-out (reconcile effect below) just cleared the selection deliberately, and
      // re-selecting the token's org_id here would ping-pong straight back into it.
      if (initialToken && readActiveOrgCookie() !== ACTIVE_ORG_UNSUPPORTED_SENTINEL) {
        const tokenOrgId = getJwtOrgId(initialToken);
        if (tokenOrgId && !rejectedOrgIdsRef.current.has(tokenOrgId)) {
          setSelectedOrgAuth0Id(tokenOrgId);
        }
      }
      return;
    }
    // The cookie names a different, adoptable org — the reconcile effect below is about to
    // re-point the selection in this very commit. Skip fetching a token for the org we are
    // leaving: the fetch would be wasted, and worse, auth0-spa-js dedupes concurrent
    // getTokenSilently calls by (clientId, audience, scope) — organization is NOT part of the
    // in-flight key — so the doomed fetch's result gets handed to the NEXT org's request too
    // (observed live on test-j: the transition load hung on the shell loader because the new
    // org's "token" carried the old org's org_id and the coherence gate refused it, forever).
    const cookieOrgId = readActiveOrgCookie();
    if (
      cookieOrgId &&
      cookieOrgId !== selectedOrgAuth0Id &&
      cookieOrgId !== ACTIVE_ORG_UNSUPPORTED_SENTINEL &&
      !rejectedOrgIdsRef.current.has(cookieOrgId)
    ) {
      return;
    }
    const fetchOrgToken = async () => {
      const targetOrgId = selectedOrgAuth0Id;
      try {
        // cacheMode: 'off' is required here. auth0-spa-js's token cache key is
        // (clientId, audience, scope) only — it does NOT include `organization` — so without
        // this, a cache entry written by AuthTokenProvider's bare (no-organization)
        // getAccessTokenSilently() call above is indistinguishable from an org-scoped one and
        // gets returned here unchanged. The resulting "orgToken" then carries no org_id claim,
        // so the backend can never resolve the user for the selected organization (DL-61312).
        let token = await getAccessTokenSilently({
          authorizationParams: { organization: targetOrgId },
          cacheMode: 'off',
        });
        // Stale resolution: the selection moved to another org while this fetch was in
        // flight (see selectedOrgRef above). Handing this token out would fetch and cache
        // the WRONG org's data under the new org's query keys — discard it; the effect
        // re-run for the new org owns the session now.
        if (selectedOrgRef.current !== targetOrgId) return;
        // auth0-spa-js dedupes concurrent getTokenSilently calls by (clientId, audience,
        // scope) — organization is NOT part of the in-flight dedupe key (same blindness as
        // its cache key, see the cacheMode comment above) — so a call racing another org's
        // fetch can be handed THAT org's token even with cacheMode: 'off'. Retry until the
        // token actually belongs to the requested org; by then the colliding transaction has
        // finished, so the retry starts a fresh, non-deduped round trip. Bounded so a
        // persistent mismatch degrades into the normal failure path instead of looping.
        let retries = 0;
        while (getJwtOrgId(token) !== targetOrgId && retries < MAX_ORG_TOKEN_FAILURES) {
          retries++;
          token = await getAccessTokenSilently({
            authorizationParams: { organization: targetOrgId },
            cacheMode: 'off',
          });
          if (selectedOrgRef.current !== targetOrgId) return;
        }
        if (getJwtOrgId(token) !== targetOrgId) {
          throw new Error(
            `org-scoped token resolution kept returning a different organization after ${retries} retries`
          );
        }
        orgTokenFailuresRef.current.delete(targetOrgId);
        setOrgToken(token);
        setDelogueApiToken(token);
      } catch (e) {
        // A stale failure must not touch the new org's state either — without this guard,
        // the removeItem() below would wipe the selection the user just switched TO because
        // a fetch for the org they switched FROM happened to fail afterwards.
        if (selectedOrgRef.current !== targetOrgId) return;
        // Deliberately not silent: this swallowed error cost multiple QA cycles on
        // DL-61312 — every downstream failure looked identical without it.
        // eslint-disable-next-line no-console -- intentional: org-token failures were silent, costing QA cycles (DL-61312)
        console.warn('Failed to acquire org-scoped token for', targetOrgId, e);
        const failures = (orgTokenFailuresRef.current.get(targetOrgId) ?? 0) + 1;
        orgTokenFailuresRef.current.set(targetOrgId, failures);
        if (failures >= MAX_ORG_TOKEN_FAILURES) rejectOrgId(targetOrgId);
        removeItem();
        setOrgToken(null);
        setDelogueApiToken(null);
      }
    };
    fetchOrgToken();
  }, [
    isAuthenticated,
    isLoading,
    selectedOrgAuth0Id,
    initialToken,
    getAccessTokenSilently,
    removeItem,
    setSelectedOrgAuth0Id,
  ]);

  // Validate the stored org still belongs to the current user's accessible orgs
  const { data: users } = useLoginUsers();
  useEffect(() => {
    if (!users || !selectedOrgAuth0Id) return;
    const exists = users.some(u => u.organization.auth0OrgId === selectedOrgAuth0Id);
    if (!exists) {
      // Remember the rejection so the auto-detect effect above doesn't immediately
      // re-select the same org from the token's org_id claim (render/token loop).
      rejectOrgId(selectedOrgAuth0Id);
      removeItem();
      setOrgToken(null);
      setDelogueApiToken(null);
    }
  }, [users, selectedOrgAuth0Id, removeItem]);

  // Reconcile with the cross-app "active org" signal (DL-61837, R6): legacy's "Switch Company"
  // now round-trips Auth0 and refreshes the shared activeOrgAuth0Id cookie on every switch. Without
  // this, D2 keeps whatever org it had selected indefinitely — the stored value never re-derives,
  // so a legacy-side switch never propagated here (Testmo 2507007 step 2). The membership check
  // above still runs afterward and drops it if the cookie names a non-member org; rejectedOrgIds
  // guards against re-adopting an org just backed out of via clearSelectedOrg.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    const cookieOrgId = readActiveOrgCookie();
    if (!cookieOrgId || cookieOrgId === selectedOrgAuth0Id) return;
    // Legacy switched to a context with no Auth0 organization at all — a legacy supplier
    // account, or an org never synced to Auth0 (DL-61837 follow-up). D2 cannot represent
    // that context, so this is a full org-session sign-out, not a follow: drop the persisted
    // selection (the transition effect above tears down tokens + cached data) and land on
    // the org picker. The Auth0 session survives on purpose — the user is still legitimately
    // signed in on legacy, and killing the shared session would back-channel-log-out the
    // very legacy session they just switched to.
    if (cookieOrgId === ACTIVE_ORG_UNSUPPORTED_SENTINEL) {
      if (selectedOrgAuth0Id) removeItem();
      return;
    }
    if (rejectedOrgIdsRef.current.has(cookieOrgId)) return;
    setSelectedOrgAuth0Id(cookieOrgId);
  }, [isAuthenticated, isLoading, selectedOrgAuth0Id, setSelectedOrgAuth0Id, removeItem]);

  const selectOrg = (auth0OrgId: string) => {
    setSelectedOrgAuth0Id(auth0OrgId);
    // Keep the shared cookie in step with an explicit D2-side switch too, so the reconciliation
    // effect above can't immediately revert it back to a now-stale legacy value on the next load.
    writeActiveOrgCookie(auth0OrgId);
  };
  const clearSelectedOrg = () => {
    // Mark the org being backed out of as rejected so neither the JWT auto-detect effect above
    // nor SwitchUserFederated's single-org auto-select immediately re-select the same org that
    // just failed to resolve (DL-61312 round 3 — "Choose another organization" looped back into
    // the identical failure for a single-org account).
    if (selectedOrgAuth0Id) rejectOrgId(selectedOrgAuth0Id);
    removeItem();
    setOrgToken(null);
    setDelogueApiToken(null);
    // useLogout calls clearSelectedOrg unconditionally before attempting /betalogin/logout, so
    // this also covers the SDK-only logout fallback when that request fails — either way the
    // shared cookie can't outlive the session and auto-select a stale org for whoever logs into
    // D2 next on this browser (Greptile catch on PR #14018).
    clearActiveOrgCookie();
  };

  // Render-time coherence gate: never hand out a token whose org_id doesn't match the current
  // selection. State updates and effects can lag a selection change by a render (React runs
  // child effects before parent effects, so a gated query can fire BEFORE the transition
  // teardown above nulls the token) — without this gate that window fetches the OLD org's
  // data under the NEW org's query key, and the persisted cache then serves it for 24h
  // (DL-61837 QA regression, hg's 8-step repro).
  const coherentOrgToken =
    orgToken && selectedOrgAuth0Id && getJwtOrgId(orgToken) === selectedOrgAuth0Id
      ? orgToken
      : null;

  return (
    <SelectedOrgContext.Provider
      value={{
        selectedOrgAuth0Id,
        orgToken: coherentOrgToken,
        selectOrg,
        clearSelectedOrg,
        rejectedOrgIds,
        isLoggingOut,
        beginLogout,
        isImpersonating: false,
      }}
    >
      {children}
    </SelectedOrgContext.Provider>
  );
};

// DL-64690: legacy already minted a delegated token, so there is no Auth0 round trip to do and
// no org to select — the token is org-scoped by the exchange Action. selectedOrgAuth0Id stays null
// deliberately: it is what Iframe.tsx appends as _auth0org, and the superadmin is not a member of
// the customer's Auth0 org, so a bridge attempt would fail.
const ImpersonationSessionProvider = ({
  token,
  children,
}: {
  token: string;
  children: React.ReactNode;
}) => {
  setDelogueApiToken(token);

  return (
    <AuthTokenContext.Provider value={{ isLoading: false, isAuthenticated: true, token }}>
      <SelectedOrgContext.Provider
        value={{
          selectedOrgAuth0Id: null,
          orgToken: token,
          selectOrg: () => undefined,
          // Switching user or org is out of scope: a superadmin logs out and restarts from the
          // legacy picker.
          clearSelectedOrg: () => undefined,
          rejectedOrgIds: new Set(),
          isLoggingOut: false,
          beginLogout: () => undefined,
          isImpersonating: true,
        }}
      >
        {children}
      </SelectedOrgContext.Provider>
    </AuthTokenContext.Provider>
  );
};

type AuthProviderProps = {
  children: React.ReactNode;
};

// NB: deep-link restoration after the Auth0 round trip is deliberately NOT done here. This
// provider sits ABOVE <RouterProvider> (see main.tsx), so it has no router hook, and importing
// the data-router instance to navigate would create a static import cycle
// (providers -> routes -> AuthLoader -> … -> components -> providers), which the architecture
// gate (`yarn arch`, no-circular) rejects. Instead useLogin stashes the intended path in
// sessionStorage before the redirect and AuthLoaderFederated (which lives INSIDE the router)
// consumes it via useNavigate once authenticated. The SDK's default onRedirectCallback still
// strips the ?code/&state params by replacing the URL with window.location.pathname ("/").
export const AuthProviderFederated = ({ children }: AuthProviderProps) => {
  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      // DL-61835 (R4): pin these explicitly rather than relying on the SDK defaults (which happen
      // to already be these values today). In-memory-only + no refresh tokens is load-bearing for
      // the whole session-invalidation design — it forces every D2 page load through a live
      // prompt=none round trip against the current Auth0 session, so a JWT can never outlive an
      // auth change (login as a different user, logout) by more than one navigation. A refresh
      // token would mint new JWTs without ever consulting the session, defeating that invariant.
      cacheLocation="memory"
      useRefreshTokens={false}
    >
      {impersonationToken ? (
        <ImpersonationSessionProvider token={impersonationToken}>
          {children}
        </ImpersonationSessionProvider>
      ) : (
        <AuthTokenProvider>
          <OrgProvider>{children}</OrgProvider>
        </AuthTokenProvider>
      )}
    </Auth0Provider>
  );
};
